import CalcBreakpoint from 'app/components/layout/CalcBreakpoint';
import { GetViewport } from 'app/components/layout/ViewportContext';
import { CALL_LAYOUT_WIDE_BREAKPOINT, CALL_LAYOUT_WIDE_VERITCAL_BREAKPOINT } from 'app/constants/Breakpoints';
import { CallVideoStateConsumer, ICallVideoState } from 'app/containers/call/CallVideoStateContext';
import useLessonUIState from 'app/containers/call/useLessonUIState';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { usePeers } from 'app/containers/rtc/PeersContext';
import { BookingState, BookingTypeSettings } from 'app/models/rtc/booking/constants';
import { CallBar, CallTab, CallVideoPlacement, ICallBreakpoint } from 'app/models/ui/call/constants';
import { defaultLessonUIState, ILessonUIState } from 'app/models/ui/call/lesson/constants';
import { getTab, isInContentVideosPresent, isInContentVideosVisible } from 'app/models/ui/call/selectors';
import { RTC_DISPLAY_ASPECT_RATIO } from 'app/services/rtc/constants';
import { useIsWebRTCSupported } from 'app/containers/rtc/BookingContext/useRequiredFeatures';
import { AppState } from 'app/store';
import { isShallowEqual } from 'app/utils/object';
import React, { createContext, ReactNode, useContext } from 'react';
import { connect } from 'react-redux';
import { defaultMemoize } from 'reselect';
import { ISize } from 'app/components/layout/useMeasure';

interface IDeps {
  bookingState: BookingState;
  isWebRTCSupported: boolean;
  lessonUIState: ILessonUIState;
  peerCount: number;
  videoState: ICallVideoState;
}

interface IProps {
  inContentVideosPresent?: boolean;
  inContentVideosVisible?: boolean;
  selectedTab?: CallTab;
}

function computeCallBreakpoint(
  { height = 0, width = 0 }: Partial<ISize> = {},
  {
    bookingState,
    inContentVideosPresent = false,
    inContentVideosVisible = false,
    selectedTab = CallTab.Content,
    videoState,
    lessonUIState = defaultLessonUIState,
    peerCount = 0,
    isWebRTCSupported = false,
  }: Partial<IDeps & IProps> = {},
): ICallBreakpoint {
  const contentTabSelected = selectedTab === CallTab.Content;
  const videoEnabled = Boolean(videoState && videoState.showVideoUI && isWebRTCSupported);
  const hasPeers = peerCount > 0;

  if (!bookingState) {
    return {
      videoEnabled,
      lessonUIState,
      tab: CallTab.Content,
      bar: CallBar.None,
      videoPlacement: CallVideoPlacement.None,
      bigVideo: false,
      pushContentDown: false,
      hasPeers,
    };
  } else {
    // Booking type can determine whether to show sidebar (or tab bar on mobile)
    const { showSidebar } = bookingState && BookingTypeSettings[bookingState.bookingType];

    if (width >= CALL_LAYOUT_WIDE_BREAKPOINT && height >= CALL_LAYOUT_WIDE_VERITCAL_BREAKPOINT) {
      return {
        videoEnabled,
        lessonUIState,
        // override call tab beause content is always visible on desktop
        tab: contentTabSelected ? CallTab.Chat : selectedTab,
        bar: showSidebar ? CallBar.Side : CallBar.None,
        videoPlacement: !showSidebar
          ? CallVideoPlacement.None
          : inContentVideosVisible
          ? CallVideoPlacement.InContent
          : CallVideoPlacement.InSidebar,
        bigVideo: true,
        pushContentDown: false,
        hasPeers,
      };
    } else {
      return {
        videoEnabled,
        lessonUIState,
        tab: selectedTab,
        bar: showSidebar ? CallBar.Top : CallBar.None,
        videoPlacement: !showSidebar
          ? CallVideoPlacement.None
          : contentTabSelected && inContentVideosVisible
          ? CallVideoPlacement.InContent
          : CallVideoPlacement.Sticky,

        // In chat and notes, don't allow the video to take too much
        // screen estate for other UI elements than the video
        bigVideo: !contentTabSelected && height > (width / RTC_DISPLAY_ASPECT_RATIO) * 2.5,

        // In case in-content video is present we don't need to push
        // down content because the in-content video is alredy taking up
        // vertical space. This assumes that the in-content-video is
        // always around the top of the page.  If the in-content video
        // is not present, we need to push content down so that when
        // scrolled all the way up it doesn't cover page content.
        pushContentDown: !inContentVideosPresent,
        hasPeers,
      };
    }
  }
}

const defaultBreakpoint = computeCallBreakpoint();

const mapStateToProps = () => {
  const memoizeResult = defaultMemoize((x: ICallBreakpoint) => x, isShallowEqual);
  const memoizedCallBreakpoint = (size: ISize, props: IDeps) => memoizeResult(computeCallBreakpoint(size, props));

  return (state: AppState) => ({
    init:                    defaultBreakpoint,
    calc:                    memoizedCallBreakpoint,
    inContentVideosPresent:  isInContentVideosPresent(state),
    inContentVideosVisible:  isInContentVideosVisible(state),
    selectedTab:             getTab(state),
  });
};

const CallBreakpointContext = createContext<ICallBreakpoint>(defaultBreakpoint);

const CallBreakpointInjectDependencies = ({ children, ...props }: IProps & { children: ReactNode }) => {
  const bookingState = useBookingState();
  const peers = usePeers();
  const lessonUIState = useLessonUIState();
  const isWebRTCSupported = useIsWebRTCSupported();

  return (
    <GetViewport>{(size) =>
      <CallVideoStateConsumer>{(videoState) =>
        <CalcBreakpoint
          {...props}
          {...size}
          bookingState={bookingState}
          lessonUIState={lessonUIState}
          videoState={videoState}
          peerCount={peers.size}
          isWebRTCSupported={isWebRTCSupported}
        >
          {(breakpoint: ICallBreakpoint) =>
            <CallBreakpointContext.Provider value={breakpoint}>
              {children}
            </CallBreakpointContext.Provider>}
        </CalcBreakpoint>
      }</CallVideoStateConsumer>
    }</GetViewport>
  );
};

export const SetCallBreakpoint = connect(mapStateToProps, null)(CallBreakpointInjectDependencies);

export const GetCallBreakpoint = ({ children }: { children: (callBreakpoint: ICallBreakpoint) => ReactNode }) =>
  <CallBreakpointContext.Consumer>{(breakpoint) =>
    children(breakpoint)
  }</CallBreakpointContext.Consumer>;

export const useCallBreakpoint = () => useContext(CallBreakpointContext);
